import type { AnalysisBuffer } from '../types/image.js';
import type { NoiseMetrics } from '../types/metrics.js';
import { mean, median, medianAbsoluteDeviation } from '../geometry/statistics.js';

/**
 * Noise estimation.
 *
 * A 3x3 median removes most of the noise while preserving edges, so the
 * residual `luma - median3x3` is a decent, cheap noise proxy. The scaling
 * constant converts the median absolute deviation of that residual into an
 * approximate standard deviation of the sensor noise.
 */
const RESIDUAL_TO_SIGMA = 1.4826;

export const computeNoise = (buffer: AnalysisBuffer): NoiseMetrics => {
  const { luma, width, height } = buffer;
  if (width < 3 || height < 3) return { sigma: 0, medianDeviation: 0 };

  const residuals: number[] = [];
  const neighbourhood = new Array<number>(9);

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      let index = 0;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          neighbourhood[index] = luma[(y + dy) * width + (x + dx)] as number;
          index += 1;
        }
      }
      const localMedian = median(neighbourhood);
      residuals.push((luma[y * width + x] as number) - localMedian);
    }
  }

  if (residuals.length === 0) return { sigma: 0, medianDeviation: 0 };

  const mad = medianAbsoluteDeviation(residuals);
  return {
    sigma: mad * RESIDUAL_TO_SIGMA,
    medianDeviation: mean(residuals.map(Math.abs)) / 255,
  };
};
